import React, { useState } from 'react'
import { Button, Collapse, List, Space, Tooltip, Typography } from 'antd'
import { EditOutlined, FileOutlined, CheckCircleOutlined, CloseCircleOutlined, PlusOutlined } from '@ant-design/icons'
import OverruleReasoningModal from './OverruleReasoningModal'
import { useSelector, useDispatch } from 'react-redux'
import submissionSelectors from '../../../../../store/selectors/submission.selectors'
import referenceReasoningsSelectors from '../../../../../store/selectors/reference-reasonings.selectors'
import { referenceReasoningsActions } from '../../../../../store/slices/reference-reasonings.slice'
import { useAssignmentAccess } from '../../access/Access'
import { Perm, State } from '../../../../../constants/assignment-permissions'
import { ContentRules } from '../../access/access-rules/assignment-rules'
import { useThemeColors } from '../../../../../hooks/useThemeColors'

const { Text } = Typography
const { Panel } = Collapse

/**
 * Component for displaying reference reasonings (overrule examples) related to a criterion
 */
const ReferenceReasonings = ({ 
    references, 
    token, 
    layerId, 
    patternId, 
    criterion,
    onOpenFile
}) => {
    const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)
    const openedCollapseIds = useSelector(referenceReasoningsSelectors.openedCollapseIds)
    const dispatch = useDispatch()
    const colors = useThemeColors()

    // Generate a unique ID for this criterion's collapse panel
    const collapseId = `reference-reasoning-${criterion.key}-${patternId}`
    
    const [selectedReference, setSelectedReference] = useState(null)
    const [showOverruleModal, setShowOverruleModal] = useState(false)
    const [hoveredItem, setHoveredItem] = useState(null)
    
    // Check if there's a current item in references
    const hasCurrentItem = references?.some(ref => ref.isCurrent)
    
    // Handle editing a reference (opens OverruleReasoningModal)
    const handleEditReference = (reference, e) => {
        e.stopPropagation() // Prevent list item click event
        setSelectedReference(reference)
        setShowOverruleModal(true)
    }
    
    // Handle opening a file in the drawer
    const handleOpenFile = (reference, e) => {
        e.stopPropagation() // Prevent list item click event
        if (onOpenFile) {
            onOpenFile(reference.fileId)
        }
    }
    
    // Handle overruling the current file
    const handleOverruleCurrent = () => {
        // Create a minimal reference object for the current file
        setSelectedReference({
            isCurrent: true,
            fileId: openedSubmissionId
        })
        setShowOverruleModal(true)
    }
    
    // Close the overrule modal
    const handleCloseOverruleModal = () => {
        setShowOverruleModal(false)
        setSelectedReference(null)
    }
    
    // Handle collapse state change
    const handleCollapseChange = keys => {
        if (keys.includes(collapseId)) {
            // Panel is being opened
            dispatch(referenceReasoningsActions.toggleCollapsePanel(collapseId))
        } else {
            // Panel is being closed
            dispatch(referenceReasoningsActions.toggleCollapsePanel(collapseId))
        }
    }

    const { hasAccess: hasEditingPermission } = useAssignmentAccess(ContentRules.EDIT_GRADINGS, token)

    const overruleCurrentButton = !hasCurrentItem && hasEditingPermission && (
        <Button 
            type="dashed" 
            icon={<PlusOutlined />} 
            size="small" 
            style={{ width: '100%', marginTop: '8px' }}
            onClick={handleOverruleCurrent}
        >
            Overrule current
        </Button>
    )
    
    // Check if this panel should be opened
    const isCollapsePanelOpen = openedCollapseIds.includes(collapseId)
    
    return (
        <>
            <Collapse 
                ghost 
                size='small' 
                activeKey={isCollapsePanelOpen ? [collapseId] : []}
                onChange={handleCollapseChange}
            >
                <Panel 
                    header={<Text>Reference Reasonings ({references.length})</Text>} 
                    key={collapseId}
                >
                    {references.length > 0 ? <List
                        size="small"
                        dataSource={references}
                        renderItem={reference => (
                            <List.Item 
                                //style={{ padding: '8px 16px' }}
                                onMouseEnter={() => setHoveredItem(reference.fileId)}
                                onMouseLeave={() => setHoveredItem(null)}
                                actions={
                                    hoveredItem === reference.fileId ? [
                                        <Tooltip title="Edit Overrule" key="edit">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={(e) => handleEditReference(reference, e)}
                                            />
                                        </Tooltip>,
                                        <Tooltip title="Open File" key="open">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<FileOutlined />}
                                                onClick={(e) => handleOpenFile(reference, e)}
                                            />
                                        </Tooltip>
                                    ] : []
                                }
                            >
                                <Space>
                                    {reference.existingOverrule && (
                                        reference.existingOverrule.criterion_met ? <CheckCircleOutlined title='Criterion Is Met' style={{ color: colors.success }} /> : <CloseCircleOutlined title='Criterion Is Not Met' style={{ color: colors.error }} />
                                    )}
                                    <Text strong={reference.isCurrent}>{reference.fileName} {reference.isCurrent && '(current)'}</Text>
                                </Space>
                            </List.Item>
                        )}
                        footer={overruleCurrentButton}
                    /> : null}
                    {references.length === 0 && overruleCurrentButton}
                </Panel>
            </Collapse>
            
            {showOverruleModal && selectedReference && (
                <OverruleReasoningModal
                    onCancel={handleCloseOverruleModal}
                    criterion={{
                        ...criterion,
                        key: criterion.key,
                        originalReasoning: criterion.reasoning
                    }}
                    token={token}
                    layerId={layerId}
                    patternId={patternId}
                    fileId={selectedReference.fileId}
                    existingOverrule={selectedReference.existingOverrule}
                />
            )}
        </>
    )
}

export default ReferenceReasonings 